import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Maximize, Minimize, Pause, Play } from "lucide-react";
import { Api } from "../lib/api.js";

export interface HlsPlayerHandle {
  video: HTMLVideoElement | null;
}

interface HlsPlayerProps {
  src: string;
  onReady?: (video: HTMLVideoElement) => void;
  onError?: (err: unknown) => void;
  onTimeUpdate?: (currentS: number, durationS: number) => void;
  onEnded?: () => void;
  autoplay?: boolean;
  poster?: string | null;
  /** Resume position in seconds. Applied once the ready region covers it —
   *  with a growing transcode that can be well after metadata loads. */
  startAt?: number;
  /** Optional subtitle track — WebVTT URL + language code + label. Mounted as
   *  a <track> element so the native player picker can enable it. */
  subtitle?: { url: string; lang: string; label: string } | null;
  /**
   * Real runtime from the server's ffprobe, for HLS sessions that are still
   * being transcoded. When set (and the src is an HLS session), the native
   * controls are replaced with a custom bar that shows the FULL length of
   * the video immediately — the native scrubber tied to a growing EVENT
   * playlist "grows" as the transcode runs, which reads as broken. The gold
   * fill marks the ready region; seeks clamp to its edge, so the timeline
   * can't be touched past what exists yet. Once the playlist completes
   * (ENDLIST → duration reaches the probe), native controls come back.
   */
  fullDurationS?: number | null;
}

function fmt(s: number): string {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
}

function seekableEnd(video: HTMLVideoElement): number {
  return video.seekable.length ? video.seekable.end(video.seekable.length - 1) : 0;
}

export function HlsPlayer({
  src,
  onReady,
  onError,
  onTimeUpdate,
  onEnded,
  autoplay = true,
  poster,
  startAt = 0,
  subtitle = null,
  fullDurationS = null,
}: HlsPlayerProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Offset sessions begin their media timeline at the seek keyframe, which can
  // precede the requested source position when video is stream-copied. The
  // server reports that real origin; convert all UI/progress timestamps with it.
  const requestedOffsetS = src.includes("/api/hls/session.m3u8")
    ? Math.max(0, Number(new URLSearchParams(src.split("?")[1] ?? "").get("start")) || 0)
    : 0;
  const offsetRef = useRef(requestedOffsetS);
  const [timelineOffsetS, setTimelineOffsetS] = useState(requestedOffsetS);
  // Custom bar only for still-growing HLS sessions with a known runtime.
  const wantsCustom = fullDurationS !== null && fullDurationS > 0 && src.includes("/api/hls/");
  const [customControls, setCustomControls] = useState(wantsCustom);
  const [progress, setProgress] = useState({ t: requestedOffsetS, ready: requestedOffsetS });
  const [paused, setPaused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const scrubRef = useRef<HTMLDivElement | null>(null);
  const scrubbingRef = useRef(false);

  // The custom bar replaces the native controls while a transcode grows —
  // which also removes the native fullscreen button. Carry the icon state so
  // our own button can swap Maximize/Minimize (Esc exits fire this too).
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  /** Fullscreen the whole container so the custom bar comes along; iOS
   *  Safari has no element-level API, only the video's own. */
  const toggleFullscreen = (): void => {
    const container = containerRef.current;
    const video = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    if (!container) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
      return;
    }
    if (container.requestFullscreen) {
      void container.requestFullscreen().catch(() => video?.webkitEnterFullscreen?.());
    } else {
      video?.webkitEnterFullscreen?.();
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    offsetRef.current = requestedOffsetS;
    setTimelineOffsetS(requestedOffsetS);
    setProgress({ t: requestedOffsetS, ready: requestedOffsetS });

    // Deep-resume: with a growing session the ready region may not cover
    // startAt for minutes — apply it the first time it fits, not just at
    // manifest time (when the guard almost always fails).
    let startApplied = false;
    const seekToStart = (): void => {
      if (startApplied) return;
      const offsetS = offsetRef.current;
      const target = Math.max(0, startAt - offsetS);
      if (target < 1) { startApplied = true; return; }
      const dur = fullDurationS !== null ? fullDurationS - offsetS : video.duration;
      if (!dur || target >= dur - 5) { startApplied = true; return; }
      const ready = seekableEnd(video) || video.duration || 0;
      if (ready >= target + 2) {
        try { video.currentTime = target; startApplied = true; } catch { /* ignore */ }
      }
    };

    const resolveMediaStart = async (): Promise<void> => {
      if (requestedOffsetS <= 0 || !src.includes("/api/hls/session.m3u8")) {
        seekToStart();
        return;
      }
      const params = new URLSearchParams(src.split("?")[1] ?? "");
      const sourceUrl = params.get("url");
      const audioRaw = params.get("audio");
      if (sourceUrl) {
        try {
          const status = await Api.hls.status(
            sourceUrl,
            audioRaw === null ? null : Number(audioRaw),
            params.get("v"),
            String(requestedOffsetS),
          );
          if (cancelled) return;
          const mediaStart = status.media_start_s;
          if (typeof mediaStart === "number" && Number.isFinite(mediaStart) && mediaStart >= 0) {
            offsetRef.current = mediaStart;
            setTimelineOffsetS(mediaStart);
            setProgress({ t: mediaStart, ready: mediaStart });
          }
        } catch {
          // The requested offset remains a safe fallback on an older server.
        }
      }
      if (!cancelled) seekToStart();
    };

    const readyToPlay = (): void => {
      void resolveMediaStart().finally(() => {
        if (cancelled) return;
        onReady?.(video);
        if (autoplay) void video.play().catch(() => {});
      });
    };

    let hls: Hls | null = null;
    const isHls = src.includes(".m3u8") || src.endsWith(".m3u");

    if (isHls && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, readyToPlay);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          onError?.(new Error(`HLS ${data.type}: ${data.details}`));
        }
      });
    } else if (isHls && video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS.
      video.src = src;
      video.addEventListener(
        "loadedmetadata",
        readyToPlay,
        { once: true },
      );
    } else {
      // Non-HLS source — plain mp4 or similar.
      video.src = src;
      video.addEventListener(
        "loadedmetadata",
        readyToPlay,
        { once: true },
      );
    }

    const onTime = () => onTimeUpdate?.(
      video.currentTime + offsetRef.current,
      fullDurationS ?? ((video.duration || 0) + offsetRef.current),
    );
    const onEnd = () => onEnded?.();
    const onProgress = () => {
      const offsetS = offsetRef.current;
      const ready = seekableEnd(video);
      setProgress({ t: video.currentTime + offsetS, ready: ready + offsetS });
      setPaused(video.paused);
      seekToStart(); // no-op once applied
      // Session complete: playlist got its ENDLIST, duration is the remainder
      // of the source (including any copied GOP pre-roll).
      if (
        fullDurationS !== null &&
        video.duration >= fullDurationS - offsetS - 2 &&
        ready >= fullDurationS - offsetS - 2
      ) {
        setCustomControls(false);
      }
    };
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("ended", onEnd);
    video.addEventListener("progress", onProgress);
    video.addEventListener("durationchange", onProgress);
    video.addEventListener("play", onProgress);
    video.addEventListener("pause", onProgress);

    return () => {
      cancelled = true;
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("ended", onEnd);
      video.removeEventListener("progress", onProgress);
      video.removeEventListener("durationchange", onProgress);
      video.removeEventListener("play", onProgress);
      video.removeEventListener("pause", onProgress);
      hls?.destroy();
      try {
        video.pause();
        video.removeAttribute("src");
        video.load();
      } catch {
        /* ignore */
      }
    };
  }, [src]);

  // Auto-enable the subtitle track when it loads. React re-uses the same
  // <video> element across renders so adding/removing a <track> child works,
  // but we have to toggle textTrack.mode="showing" ourselves — the native
  // default is "disabled" for cross-origin <track> elements.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !subtitle) return;
    const enable = (): void => {
      for (const t of Array.from(video.textTracks)) {
        t.mode = t.kind === "subtitles" || t.kind === "captions" ? "showing" : t.mode;
      }
    };
    // textTracks list isn't populated until the <track> loads.
    const t = setTimeout(enable, 100);
    video.addEventListener("loadeddata", enable);
    return () => {
      clearTimeout(t);
      video.removeEventListener("loadeddata", enable);
    };
  }, [subtitle?.url]);

  // Scrubber: full-length track, seeks clamped to the ready edge — the
  // timeline is honest about length but can't be touched past what exists.
  const seekFromPointer = (clientX: number): void => {
    const video = videoRef.current;
    const track = scrubRef.current;
    if (!video || !track || !fullDurationS) return;
    const rect = track.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const edge = Math.max(0, progress.ready - timelineOffsetS - 1);
    if (edge <= 0) return; // nothing ready to watch at all yet
    video.currentTime = Math.min(Math.max(0, frac * fullDurationS - timelineOffsetS), edge);
  };

  const full = fullDurationS ?? 0;
  const readyFrac = full > 0 ? Math.min(1, progress.ready / full) : 0;
  const playFrac = full > 0 ? Math.min(1, progress.t / full) : 0;
  const growing = customControls && full > 0 && progress.ready < full - 2;

  // The <video> needs crossOrigin="anonymous" for subtitle <track> elements
  // to load cross-origin. That forces the `poster` attribute to also need
  // CORS headers — but TMDB's image CDN doesn't send them. Render the
  // poster as a plain <img> behind the video instead.
  return (
    <div ref={containerRef} className="relative w-full h-full bg-ink">
      {poster && (
        <img
          src={poster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          referrerPolicy="no-referrer"
        />
      )}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full"
        controls={!customControls}
        playsInline
        onClick={(e) => {
          if (!customControls) return;
          const v = e.currentTarget;
          if (v.paused) void v.play().catch(() => {}); else v.pause();
        }}
      >
        {subtitle && (
          <track
            kind="subtitles"
            src={subtitle.url}
            srcLang={subtitle.lang}
            label={subtitle.label}
            default
          />
        )}
      </video>

      {customControls && full > 0 && (
        <div className="absolute bottom-0 inset-x-0 px-3 pb-2 pt-8 bg-gradient-to-t from-black/80 to-transparent">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const v = videoRef.current;
                if (!v) return;
                if (v.paused) void v.play().catch(() => {}); else v.pause();
              }}
              className="text-ivory hover:text-gold transition shrink-0"
              title={paused ? "Play" : "Pause"}
            >
              {paused ? <Play size={18} /> : <Pause size={18} />}
            </button>
            <span className="text-[11px] text-parchment font-mono shrink-0">
              {fmt(progress.t)} / {fmt(full)}
            </span>
            <div
              ref={scrubRef}
              className="relative flex-1 h-4 flex items-center cursor-pointer group"
              onPointerDown={(e) => {
                scrubbingRef.current = true;
                e.currentTarget.setPointerCapture(e.pointerId);
                seekFromPointer(e.clientX);
              }}
              onPointerMove={(e) => {
                if (scrubbingRef.current) seekFromPointer(e.clientX);
              }}
              onPointerUp={() => { scrubbingRef.current = false; }}
              onPointerCancel={() => { scrubbingRef.current = false; }}
              title={growing ? `Ready to ${fmt(progress.ready)} — the rest is still transcoding` : "Seek"}
            >
              <div className="relative h-1.5 w-full rounded-full bg-white/15 overflow-hidden">
                {/* Ready region — the part that may be touched. */}
                <div
                  className="absolute inset-y-0 left-0 bg-gold/60"
                  style={{ width: `${readyFrac * 100}%` }}
                />
                {/* Playhead fill inside the ready region. */}
                <div
                  className="absolute inset-y-0 left-0 bg-gold"
                  style={{ width: `${playFrac * 100}%` }}
                />
              </div>
              <div
                className="absolute w-3 h-3 rounded-full bg-gold shadow -translate-x-1/2"
                style={{ left: `${playFrac * 100}%` }}
              />
            </div>
            {growing && (
              <span className="text-[11px] text-parchment/70 shrink-0 hidden sm:inline">
                ready to {fmt(progress.ready)} · transcoding…
              </span>
            )}
            <button
              onClick={toggleFullscreen}
              className="text-ivory hover:text-gold transition shrink-0"
              title={isFullscreen ? "Exit full screen" : "Full screen"}
            >
              {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
